import { format } from "date-fns";
import { Box, Typography, Button, Card, CardContent, Divider, Stack, Chip, useTheme } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import FlexBetween from 'components/FlexBetween';


const CrewSalesCard = ({ invoice, onView, index }) => {
    const theme = useTheme();

    const filteredProducts = invoice.products?.filter(
        p => p.preorderQuantity > 0 || p.directQuantity > 0
    );

    const sortedFlights = [...(invoice.selectedFlights || [])].sort((a, b) => {
        const d = new Date(a.flightDate) - new Date(b.flightDate);
        return d !== 0 ? d : a.etd.localeCompare(b.etd);
    });

    return (
        <Card variant="outlined" sx={{ borderRadius: 2 }}>
            <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                <FlexBetween alignItems="flex-start" mb={0.5}>
                    <Box>
                        <Box display="flex" alignItems="center" gap={0.75} mb={0.25} flexWrap="wrap">
                            <Typography variant="body2" color="text.secondary">
                                #{index}
                            </Typography>
                            <Typography variant="body2">
                                {invoice.crewCode}
                            </Typography>
                            <Typography variant="body2">
                                {invoice.crewName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                at {format(new Date(invoice.createdAt), "HH:mm · dd/MM/yy")}
                            </Typography>
                        </Box>
                        <Box display="flex" alignItems="center" gap={1} mt={0.25}>
                            {invoice.flightDate && (
                                <Typography variant="body2" color="text.secondary">
                                    {format(new Date(invoice.flightDate), "dd/MM/yy")}
                                </Typography>
                            )}
                            <Typography variant="body2">
                                {invoice.flightNo || '-'}
                            </Typography>
                            {invoice.crewRoute && (
                                <Typography variant="body2">
                                    {invoice.crewRoute}
                                </Typography>
                            )}
                        </Box>
                    </Box>
                    <Button
                        size="small"
                        onClick={onView}
                        sx={{ 
                            color: theme.palette.primary.dark, 
                            minWidth: 'auto', 
                            p: 0.5 
                        }}
                    >
                        <VisibilityIcon fontSize="small" />
                    </Button>
                </FlexBetween>

                {sortedFlights.length > 0 && (
                    <>
                        <Divider sx={{ my: 1 }} />
                        <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                            Flights
                        </Typography>
                        <Stack spacing={0.25}>
                            {sortedFlights.map((flight, i) => (
                                <Box key={i} display="flex" gap={1}>
                                    <Typography variant="body2" sx={{ width: 36, fontWeight: 'medium' }}>
                                        {flight.flightNo}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {format(new Date(flight.flightDate), "ddMMM")} {flight.etd}
                                    </Typography>
                                    <Typography variant="body2">
                                        {flight.dep}→{flight.arr}
                                    </Typography>
                                </Box>
                            ))}
                        </Stack>
                    </>
                )}

                {filteredProducts?.length > 0 && (
                    <>
                        <Divider sx={{ my: 1 }} />
                        <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                            Products
                        </Typography>
                        <Stack spacing={0.25}>
                            {filteredProducts.map((product, i) => {
                                const qty = (product.preorderQuantity || 0) + (product.directQuantity || 0);
                                return (
                                    <Box key={i} display="flex" justifyContent="space-between">
                                        <Typography variant="body2" noWrap sx={{ flex: 1, mr: 1 }}>
                                            {i + 1}. {product.productName}
                                        </Typography>
                                        <Chip label={`×${qty}`} size="small" sx={{ height: 18, fontSize: '0.7rem' }} />
                                    </Box>
                                );
                            })}
                        </Stack>
                    </>
                )}

                <Divider sx={{ my: 1 }} />
                <Box display="flex" gap={1}>
                    <Box flex={1} textAlign="center">
                        <Typography variant="caption" color="text.secondary" display="block">Own Commission</Typography>
                        <Typography variant="body2" fontWeight="bold" color="primary.main">
                            {invoice.eachCrewTotalCommission?.toLocaleString() || '0'}
                        </Typography>
                    </Box>
                    <Divider orientation="vertical" flexItem />
                    <Box flex={1} textAlign="center">
                        <Typography variant="caption" color="text.secondary" display="block">Direct</Typography>
                        <Typography variant="body2" fontWeight="medium">
                            {invoice.directCommission?.toLocaleString() || '0'}
                        </Typography>
                    </Box>
                    <Divider orientation="vertical" flexItem />
                    <Box flex={1} textAlign="center">
                        <Typography variant="caption" color="text.secondary" display="block">Payable</Typography>
                        <Typography variant="body2" fontWeight="bold" color="success.main">
                            {invoice.payableAmount?.toLocaleString() || '0'}
                        </Typography>
                    </Box>
                </Box>
            </CardContent>
        </Card>
    );
};

export default CrewSalesCard;
